import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Users,
  BarChart3,
  UserSearch,
  Settings,
  Video,
  LogOut,
  ShieldCheck,
  ShieldOff,
  BookOpen,
  Lock,
  Bell,
  Sun,
  Moon,
  Mail,
  CreditCard,
  PanelLeftClose,
  PanelLeftOpen } from
"lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useDirection } from "@/hooks/useDirection";
import { useOrgServices } from "@/hooks/useOrgServices";
import { ROUTE_SERVICE_MAP } from "@/lib/serviceKeys";
import { useServiceGovernance } from "@/hooks/useServiceGovernance";
import { toast } from "@/hooks/use-toast";
import { useTheme } from "next-themes";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarFooter,
  useSidebar } from
"@/components/ui/sidebar";
import { useDashboardStats } from "@/hooks/useDashboard";
import { useEmployerNotifications } from "@/hooks/useEmployerNotifications";
import { useCandidates } from "@/hooks/useCandidates";
import { isServiceModuleNavigationAvailable } from "@/lib/serviceAvailabilityState";
import { countForkHireGlobalCandidateApplications } from "@/lib/forkhire/workflow/globalCandidateVisibility";

const navItems: { icon: typeof LayoutDashboard; labelKey: string; mobileLabelKey?: string; path: string; badgeKey?: 'jobs' | 'interviews' | 'candidates'; adminOnly?: boolean; children?: { icon: typeof LayoutDashboard; labelKey: string; path: string }[] }[] = [
{ icon: LayoutDashboard, labelKey: "hire.nav.dashboard", path: "/dashboard" },
{ icon: Briefcase, labelKey: "hire.nav.jobs", path: "/jobs", badgeKey: "jobs" },
{ icon: Video, labelKey: "hire.nav.interviews", mobileLabelKey: "hire.nav.mobile.interviews", path: "/interviews", badgeKey: "interviews" },
{ icon: Users, labelKey: "hire.nav.candidates", path: "/candidates", badgeKey: "candidates" },
{ icon: UserSearch, labelKey: "hire.nav.talentPool", path: "/talent" },
{ icon: Mail, labelKey: "hire.nav.sentInvitations", path: "/talent/invitations" },
{ icon: BarChart3, labelKey: "hire.nav.analytics", path: "/analytics" },
{ icon: ShieldCheck, labelKey: "hire.nav.team", path: "/team", adminOnly: true },
{ icon: CreditCard, labelKey: "hire.nav.billing", path: "/settings/billing", adminOnly: true },
{ icon: Settings, labelKey: "hire.nav.settings", path: "/settings", adminOnly: true }];


/* ── Shared link/button classes — direction handled by dir attribute ── */
const navLinkBase =
"flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150 touch-target";
const footerBtnBase =
"flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors w-full touch-target";
const collapsedRailBtnBase = "mx-auto justify-center gap-0 !p-0";

const hireLogoAssets = {
  en: {
    light: "/SVG-New/Hire-black%20En.svg",
    dark: "/SVG-New/Hire-White%20En.svg",
  },
  ar: {
    light: "/SVG-New/Hire-Black%20Ar.svg",
    dark: "/SVG-New/Hire-White%20Ar.svg",
  },
} as const;

const forkHireFMarkSrc = '/brand/forkhire/mark.svg';
const hireMobilePrimaryPaths = ["/dashboard", "/jobs", "/interviews", "/candidates"];

function getHireInitials(name?: string | null) {
  return name?.
    split(" ").
    map((n) => n[0]).
    join("") || "?";
}

function isHirePathActive(pathname: string, path: string) {
  return path === "/" || path === "/settings" ? pathname === path : pathname.startsWith(path);
}

export function AppSidebar() {
  const location = useLocation();
  const { profile, role, signOut } = useAuth();
  const isAdmin = role === 'org_owner' || role === 'org_admin' || role === 'finance';
  const { state, setOpenMobile, toggleSidebar, isMobile } = useSidebar();
  const collapsed = !isMobile && state === "collapsed";
  const { t, i18n } = useTranslation();
  const { isRtl } = useDirection();
  const { data: orgServices } = useOrgServices();
  const { data: stats } = useDashboardStats();
  const { data: candidates } = useCandidates();
  const { unreadCount: notifCount } = useEmployerNotifications();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const isArabic = (i18n.resolvedLanguage ?? i18n.language)?.startsWith('ar');
  const isDark = (resolvedTheme ?? theme) === 'dark';
  const hireLogoSrc = hireLogoAssets[isArabic ? 'ar' : 'en'][isDark ? 'dark' : 'light'];
  const collapseLabel = collapsed
    ? t('common.openSidebar')
    : t('common.collapseSidebar');
  const accountPath = isAdmin ? "/organization" : "/my-account";
  const accountLabel = profile?.full_name || profile?.email || t("hire.account.title");

  // Governance: check for globally disabled services
  const govJobs = useServiceGovernance(ROUTE_SERVICE_MAP['/jobs'] || '');
  const govCandidates = useServiceGovernance(ROUTE_SERVICE_MAP['/candidates'] || '');
  const govInterviews = useServiceGovernance(ROUTE_SERVICE_MAP['/interviews'] || '');
  const govTalent = useServiceGovernance(ROUTE_SERVICE_MAP['/talent'] || '');
  const govAnalytics = useServiceGovernance(ROUTE_SERVICE_MAP['/analytics'] || '');
  const govTeam = useServiceGovernance(ROUTE_SERVICE_MAP['/team'] || '');

  const govMap: Record<string, ReturnType<typeof useServiceGovernance>> = {
    '/jobs': govJobs,
    '/candidates': govCandidates,
    '/interviews': govInterviews,
    '/talent': govTalent,
    '/talent/invitations': govTalent,
    '/analytics': govAnalytics,
    '/team': govTeam,
  };

  const badges: Record<string, number | undefined> = {
    jobs: stats?.activeJobs,
    candidates: countForkHireGlobalCandidateApplications(candidates),
    interviews: stats?.activeInterviewConfigs
  };

  const accessMap = new Map<string, boolean>();
  orgServices?.forEach((s) => accessMap.set(s.service_key, s.is_enabled));

  const isServiceEnabled = (path: string): boolean => {
    const gov = govMap[path];
    const serviceKey = ROUTE_SERVICE_MAP[path];
    if (!serviceKey) return true;
    return gov
      ? isServiceModuleNavigationAvailable(gov, accessMap.get(serviceKey) ?? true)
      : accessMap.get(serviceKey) ?? true;
  };

  const isGlobalOff = (path: string): boolean => {
    const gov = govMap[path];
    return gov?.isGlobalOff === true;
  };

  const handleNavClick = (e: React.MouseEvent, path: string) => {
    if (!isServiceEnabled(path)) {
      e.preventDefault();
      toast({
        title: t("hire.services.lockedTitle"),
        description: t("hire.services.lockedMessage"),
        variant: "destructive"
      });
      return;
    }
    setOpenMobile(false);
  };

  return (
    <Sidebar side={isRtl ? "right" : "left"} collapsible="icon">
      {/* Logo - full mark expanded, F mark collapsed */}
      <div className={cn("border-b border-sidebar-border py-4", collapsed ? "px-2" : "px-5")}>
        {collapsed ? (
          <div className="flex w-full items-center justify-center">
            <button
              type="button"
              onClick={toggleSidebar}
              className="group relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={collapseLabel}
              title={collapseLabel}>
              <img
                src={forkHireFMarkSrc}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 object-contain transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0"
              />
              <PanelLeftOpen className="absolute h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
            </button>
          </div>
        ) : (
          <div className="flex w-full items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col items-center justify-center gap-1.5">
              <img src={hireLogoSrc} alt="ForkHire" className="h-auto w-28 flex-shrink-0" />
              <p className="text-[11px] text-muted-foreground leading-none">{t("hire.sidebar.portalLabel")}</p>
            </div>
            <button
              type="button"
              onClick={toggleSidebar}
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex"
              aria-label={collapseLabel}
              title={collapseLabel}>
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Nav items */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="mt-3 gap-0.5 px-3 group-data-[collapsible=icon]:px-0">
              {navItems.filter(item => !item.adminOnly || isAdmin).map((item) => {
                const isActive =
                item.path === "/" || item.path === "/settings" ? location.pathname === item.path :
                  item.children ? location.pathname === item.path :
                  location.pathname.startsWith(item.path);
                const enabled = isServiceEnabled(item.path);
                const badgeCount = item.badgeKey ? badges[item.badgeKey] : undefined;
                const hasActiveChild = item.children?.some(c => location.pathname.startsWith(c.path));
                const itemLabel = t(item.labelKey);

                return (
                  <div key={item.path}>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive || hasActiveChild} tooltip={itemLabel}>
                      <Link
                        to={enabled ? item.path : "#"}
                        onClick={(e) => handleNavClick(e, item.path)}
                        aria-label={itemLabel}
                        title={itemLabel}
                        className={cn(
                          navLinkBase,
                          collapsed && collapsedRailBtnBase,
                          !enabled && "opacity-40 cursor-not-allowed",
                          (isActive || hasActiveChild) && enabled ?
                          "bg-primary text-primary-foreground shadow-sm" :
                          "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                        )}>
                        
                        <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                        {!collapsed && <span className="flex-1 text-[14px]">{itemLabel}</span>}
                        {enabled && badgeCount != null && badgeCount > 0 && !collapsed &&
                        <span
                          className={cn("text-[11px] font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5",
                          (isActive || hasActiveChild) ?
                          "bg-primary-foreground/20 text-primary-foreground" :
                          "bg-muted text-muted-foreground"
                          )}>
                            {badgeCount}
                          </span>
                        }
                        {!enabled && !collapsed && (
                          isGlobalOff(item.path) ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <ShieldOff className="w-3.5 h-3.5 text-destructive/60 shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[200px] text-xs">
                                {govMap[item.path]?.message || t('common.serviceUnavailable')}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Lock className="w-3.5 h-3.5 text-muted-foreground/40" />
                          )
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {/* Sub-items */}
                  {item.children && !collapsed && enabled && item.children.map((child) => {
                    const childActive = location.pathname.startsWith(child.path);
                    const childEnabled = isServiceEnabled(child.path);
                    return (
                      <SidebarMenuItem key={child.path}>
                        <SidebarMenuButton asChild isActive={childActive}>
                          <Link
                            to={childEnabled ? child.path : "#"}
                            onClick={(e) => handleNavClick(e, child.path)}
                            className={cn(
                              navLinkBase,
                              "ps-9 py-2",
                              !childEnabled && "opacity-40 cursor-not-allowed",
                              childActive && childEnabled ?
                              "bg-primary/10 text-primary font-semibold" :
                              "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                            )}>
                            <child.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1 text-[13px]">{t(child.labelKey)}</span>
                            {!childEnabled && <Lock className="w-3.5 h-3.5 text-muted-foreground/40" />}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                  </div>);

              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="border-t border-sidebar-border">
        {/* Notifications */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("hire.nav.notifications")}>
              <Link to="/notifications" className={cn(
                footerBtnBase,
                collapsed && collapsedRailBtnBase,
                location.pathname === '/notifications' && 'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground'
              )}
              aria-label={t("hire.nav.notifications")}
              title={t("hire.nav.notifications")}>
                <Bell className="w-[18px] h-[18px] flex-shrink-0" />
                {!collapsed && <span className="flex-1 text-start">{t("hire.nav.notifications")}</span>}
                {!collapsed && notifCount > 0 &&
                <span className={cn(
                  "text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1",
                  location.pathname === '/notifications'
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-destructive text-destructive-foreground'
                )}>
                    {notifCount}
                  </span>
                }
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* User card */}
        {profile && !collapsed &&
        <Link to={accountPath} className="block px-4 py-3 hover:bg-muted/60 transition-colors rounded-lg mx-2 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold flex-shrink-0">
                {profile.full_name?.
              split(" ").
              map((n) => n[0]).
              join("") || "?"}
              </div>
              <div className="min-w-0 text-start">
                <p className="text-sm font-semibold text-foreground truncate">{profile.full_name || profile.email}</p>
                {role && <p className="text-xs text-muted-foreground capitalize">{role.replace("_", " ")}</p>}
              </div>
            </div>
          </Link>
        }
        {profile && collapsed &&
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={accountPath}
                className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={accountLabel}
                title={accountLabel}>
                {profile.full_name?.
              split(" ").
              map((n) => n[0]).
              join("") || "?"}
              </Link>
            </TooltipTrigger>
            <TooltipContent side={isRtl ? "left" : "right"} className="text-xs">
              {accountLabel}
            </TooltipContent>
          </Tooltip>
        }

        {/* Language + Theme */}
        <SidebarMenu>
          {!collapsed &&
          <SidebarMenuItem>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <LanguageSwitcher variant="sidebar" />
                </div>
                <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                title={t("common.toggleTheme")}>
                  {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
              </div>
            </SidebarMenuItem>
          }

          {/* User guide */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("hire.nav.userGuide")}>
              <Link
                to="/user-guide"
                className={cn(footerBtnBase, collapsed && collapsedRailBtnBase)}
                aria-label={t("hire.nav.userGuide")}
                title={t("hire.nav.userGuide")}>
                <BookOpen className="w-[18px] h-[18px] flex-shrink-0" />
                {!collapsed && <span>{t("hire.nav.userGuide")}</span>}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Sign out */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("common.signOut")}>
              <button
                onClick={signOut}
                className={cn(footerBtnBase, collapsed && collapsedRailBtnBase)}
                aria-label={t("common.signOut")}
                title={t("common.signOut")}
                data-testid="hire-sign-out-button"
              >
                <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
                {!collapsed && <span>{t("common.signOut")}</span>}
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>);

}

export function HireMobileBottomNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const { profile, role, signOut } = useAuth();
  const isAdmin = role === 'org_owner' || role === 'org_admin' || role === 'finance';
  const { t } = useTranslation();
  const { data: orgServices } = useOrgServices();
  const { data: stats } = useDashboardStats();
  const { data: candidates } = useCandidates();
  const { unreadCount: notifCount } = useEmployerNotifications();
  const { theme, setTheme } = useTheme();

  const govJobs = useServiceGovernance(ROUTE_SERVICE_MAP['/jobs'] || '');
  const govCandidates = useServiceGovernance(ROUTE_SERVICE_MAP['/candidates'] || '');
  const govInterviews = useServiceGovernance(ROUTE_SERVICE_MAP['/interviews'] || '');
  const govTalent = useServiceGovernance(ROUTE_SERVICE_MAP['/talent'] || '');
  const govAnalytics = useServiceGovernance(ROUTE_SERVICE_MAP['/analytics'] || '');
  const govTeam = useServiceGovernance(ROUTE_SERVICE_MAP['/team'] || '');

  const govMap: Record<string, ReturnType<typeof useServiceGovernance>> = {
    '/jobs': govJobs,
    '/candidates': govCandidates,
    '/interviews': govInterviews,
    '/talent': govTalent,
    '/talent/invitations': govTalent,
    '/analytics': govAnalytics,
    '/team': govTeam,
  };

  const badges: Record<string, number | undefined> = {
    jobs: stats?.activeJobs,
    candidates: countForkHireGlobalCandidateApplications(candidates),
    interviews: stats?.activeInterviewConfigs
  };

  const accessMap = new Map<string, boolean>();
  orgServices?.forEach((s) => accessMap.set(s.service_key, s.is_enabled));

  const isServiceEnabled = (path: string): boolean => {
    const gov = govMap[path];
    const serviceKey = ROUTE_SERVICE_MAP[path];
    if (!serviceKey) return true;
    return gov
      ? isServiceModuleNavigationAvailable(gov, accessMap.get(serviceKey) ?? true)
      : accessMap.get(serviceKey) ?? true;
  };

  const isGlobalOff = (path: string): boolean => {
    const gov = govMap[path];
    return gov?.isGlobalOff === true;
  };

  const handleMobileNavClick = (event: React.MouseEvent, path: string) => {
    if (!isServiceEnabled(path)) {
      event.preventDefault();
      toast({
        title: t("hire.services.lockedTitle"),
        description: t("hire.services.lockedMessage"),
        variant: "destructive"
      });
      return;
    }
    setMoreOpen(false);
  };

  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);
  const primaryItems = hireMobilePrimaryPaths
    .map((path) => visibleItems.find((item) => item.path === path))
    .filter((item): item is (typeof navItems)[number] => Boolean(item));
  const overflowItems = visibleItems.filter((item) => !hireMobilePrimaryPaths.includes(item.path));
  const accountPath = isAdmin ? "/organization" : "/my-account";
  const accountLabel = profile?.full_name || profile?.email || t("hire.account.title");
  const initials = getHireInitials(profile?.full_name || profile?.email);
  const moreLabel = t("common.more");
  const isMoreActive =
    overflowItems.some((item) => isHirePathActive(location.pathname, item.path)) ||
    [accountPath, "/notifications", "/user-guide"].some((path) => isHirePathActive(location.pathname, path));

  const renderOverflowItem = (item: (typeof navItems)[number]) => {
    const itemLabel = t(item.labelKey);
    const active = isHirePathActive(location.pathname, item.path);
    const enabled = isServiceEnabled(item.path);
    const badgeCount = item.badgeKey ? badges[item.badgeKey] : undefined;

    return (
      <Link
        key={item.path}
        to={enabled ? item.path : "#"}
        onClick={(event) => handleMobileNavClick(event, item.path)}
        className={cn(
          "flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
          active && enabled ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          !enabled && "cursor-not-allowed opacity-50"
        )}
        aria-label={itemLabel}
        title={itemLabel}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        <span className="flex-1">{itemLabel}</span>
        {enabled && badgeCount != null && badgeCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-[11px] font-bold text-muted-foreground">
            {badgeCount}
          </span>
        )}
        {!enabled && (
          isGlobalOff(item.path) ? (
            <ShieldOff className="h-3.5 w-3.5 shrink-0 text-destructive/60" />
          ) : (
            <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
          )
        )}
      </Link>
    );
  };

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card md:hidden"
        aria-label={t("hire.nav.mobileNavigation")}
      >
        <div className="grid h-[calc(4.75rem+env(safe-area-inset-bottom))] grid-cols-5 items-start px-0.5 pb-[env(safe-area-inset-bottom)] pt-2">
          {primaryItems.map((item) => {
            const itemLabel = t(item.mobileLabelKey ?? item.labelKey);
            const active = isHirePathActive(location.pathname, item.path);
            const enabled = isServiceEnabled(item.path);
            const badgeCount = item.badgeKey ? badges[item.badgeKey] : undefined;

            return (
              <Link
                key={item.path}
                to={enabled ? item.path : "#"}
                onClick={(event) => handleMobileNavClick(event, item.path)}
                className={cn(
                  "flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-0.5 text-[11px] font-medium transition-colors",
                  active && enabled ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  !enabled && "opacity-50"
                )}
                aria-label={itemLabel}
                title={itemLabel}
              >
                <span className="relative">
                  <item.icon className="h-5 w-5" />
                  {enabled && badgeCount != null && badgeCount > 0 && (
                    <span className="absolute -end-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {badgeCount}
                    </span>
                  )}
                </span>
                <span className="max-w-full whitespace-nowrap text-center leading-none">{itemLabel}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isMoreActive && "bg-primary/10 text-primary"
            )}
            aria-label={moreLabel}
            title={moreLabel}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {initials}
            </span>
            <span className="max-w-full whitespace-nowrap text-center leading-none">{moreLabel}</span>
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[82dvh] overflow-y-auto rounded-t-2xl border-border bg-card px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 text-card-foreground md:hidden"
        >
          <SheetHeader className="text-start">
            <SheetTitle className="text-base">{accountLabel}</SheetTitle>
          </SheetHeader>
          {profile && (
            <Link
              to={accountPath}
              onClick={() => setMoreOpen(false)}
              className="mt-4 flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:bg-muted/60"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {initials}
              </span>
              <span className="min-w-0 flex-1 text-start">
                <span className="block truncate text-sm font-semibold text-foreground">{profile.full_name || profile.email}</span>
                {role && <span className="text-xs text-muted-foreground capitalize">{role.replace("_", " ")}</span>}
              </span>
            </Link>
          )}

          <div className="mt-4 grid gap-1">
            {overflowItems.map(renderOverflowItem)}
            <Link
              to="/notifications"
              onClick={() => setMoreOpen(false)}
              className={cn(
                "flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
                location.pathname === "/notifications"
                  ? "bg-primary/10 font-semibold text-primary"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
              aria-label={t("hire.nav.notifications")}
              title={t("hire.nav.notifications")}
            >
              <Bell className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t("hire.nav.notifications")}</span>
              {notifCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-destructive-foreground">
                  {notifCount}
                </span>
              )}
            </Link>
            <Link
              to="/user-guide"
              onClick={() => setMoreOpen(false)}
              className={cn(
                "flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
                location.pathname === "/user-guide"
                  ? "bg-primary/10 font-semibold text-primary"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
              aria-label={t("hire.nav.userGuide")}
              title={t("hire.nav.userGuide")}
            >
              <BookOpen className="h-4 w-4 shrink-0" />
              <span>{t("hire.nav.userGuide")}</span>
            </Link>
          </div>

          <div className="mt-4 border-t border-border pt-3">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <LanguageSwitcher variant="sidebar" />
              </div>
              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t("common.toggleTheme")}
                title={t("common.toggleTheme")}
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                setMoreOpen(false);
                signOut();
              }}
              className="mt-2 flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t("common.signOut")}
              title={t("common.signOut")}
              data-testid="hire-mobile-sign-out-button"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span>{t("common.signOut")}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
